import React, { createContext, useState, useEffect, useContext } from 'react';
import { ethers } from 'ethers';
import { formatAddress, getNetworkName } from './contractUtils';
import { isMetaMaskInstalled } from './metamaskDetector';

// Create context
const WalletContext = createContext();

// Provider component
export const WalletProvider = ({ children }) => {
  const [account, setAccount] = useState('');
  const [chainId, setChainId] = useState(null);
  const [networkName, setNetworkName] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isWalletAvailable, setIsWalletAvailable] = useState(false);

  // Check for wallet availability at initialization
  useEffect(() => {
    // Check if an Ethereum provider is available in window
    const hasEthereumProvider = isMetaMaskInstalled();
    setIsWalletAvailable(hasEthereumProvider);
  }, []);

  // Define handler functions outside of useEffect to avoid dependency issues
  const handleAccountsChanged = React.useCallback(async (accounts) => {
    if (accounts.length === 0) {
      // User disconnected their wallet
      disconnectWallet();
    } else {
      // User switched accounts - don't check against current account to avoid circular updates
      try {
        if (window.ethereum) {
          const ethersProvider = new ethers.BrowserProvider(window.ethereum);
          const currentSigner = await ethersProvider.getSigner();
          setAccount(accounts[0]);
          setSigner(currentSigner);
        }
      } catch (err) {
        console.error("Error handling account change:", err);
      }
    }
  }, []);

  const handleChainChanged = React.useCallback(async (chainIdHex) => {
    try {
      const currentChainId = parseInt(chainIdHex, 16);
      setChainId(currentChainId);
      setNetworkName(getNetworkName(currentChainId));
      
      // Refresh provider and signer on chain change
      if (window.ethereum) {
        const ethersProvider = new ethers.BrowserProvider(window.ethereum);
        setProvider(ethersProvider);
        
        // Only try to get signer if we know accounts are connected
        const accounts = await ethersProvider.listAccounts();
        if (accounts.length > 0) {
          const currentSigner = await ethersProvider.getSigner();
          setSigner(currentSigner);
        }
      }
    } catch (err) {
      console.error("Error handling chain change:", err);
    }
  }, []);

  const handleDisconnect = React.useCallback(() => {
    disconnectWallet();
  }, []);

  useEffect(() => {
    // Only proceed if Ethereum provider is available
    if (!isWalletAvailable) {
      return;
    }

    // Check if wallet is already connected
    checkConnection();

    // Setup event listeners
    try {
      if (window.ethereum) {
        window.ethereum.on('accountsChanged', handleAccountsChanged);
        window.ethereum.on('chainChanged', handleChainChanged);
        window.ethereum.on('disconnect', handleDisconnect);

        return () => {
          // Clean up listeners
          if (window.ethereum) {
            window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
            window.ethereum.removeListener('chainChanged', handleChainChanged);
            window.ethereum.removeListener('disconnect', handleDisconnect);
          }
        };
      }
    } catch (err) {
      console.error("Error setting up wallet event listeners:", err);
    }
  }, [handleAccountsChanged, handleChainChanged, handleDisconnect, isWalletAvailable]);

  const checkConnection = async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        const ethersProvider = new ethers.BrowserProvider(window.ethereum);
        setProvider(ethersProvider);

        // Get connected accounts
        const accounts = await ethersProvider.listAccounts();
        
        if (accounts.length > 0) {
          const currentSigner = await ethersProvider.getSigner();
          const address = await currentSigner.getAddress();
          
          setAccount(address);
          setSigner(currentSigner);
          setIsConnected(true);
          
          // Get network info
          const network = await ethersProvider.getNetwork();
          const currentChainId = Number(network.chainId);
          setChainId(currentChainId);
          setNetworkName(getNetworkName(currentChainId));
        }
      }
    } catch (err) {
      console.error("Error checking wallet connection:", err);
      setError(err.message);
    }
  };

  const connectWallet = async () => {
    setIsConnecting(true);
    setError(null);
    
    try {
      if (typeof window.ethereum === 'undefined') {
        throw new Error("No Ethereum wallet detected. Please install MetaMask.");
      }

      const ethersProvider = new ethers.BrowserProvider(window.ethereum);
      setProvider(ethersProvider);
      
      // Request accounts access
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      
      // Get signer and address
      const currentSigner = await ethersProvider.getSigner();
      const address = await currentSigner.getAddress();
      
      setAccount(address);
      setSigner(currentSigner);
      setIsConnected(true);
      
      // Get network info
      const network = await ethersProvider.getNetwork();
      const currentChainId = Number(network.chainId);
      setChainId(currentChainId);
      setNetworkName(getNetworkName(currentChainId));
      
      return address;
    } catch (err) {
      console.error("Error connecting to wallet:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    setAccount('');
    setSigner(null);
    setIsConnected(false);
    setChainId(null);
    setNetworkName('');
  };

  // Format account address for display
  const formattedAccount = account ? formatAddress(account) : '';

  // Contextual values to provide
  const value = {
    account,
    chainId,
    networkName,
    isConnected,
    isConnecting,
    provider,
    signer,
    error,
    formattedAccount,
    connectWallet,
    disconnectWallet,
    isWalletAvailable
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

// Custom hook for using the wallet context
export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export default WalletContext;